import React from 'react';
import { Form } from 'react-bootstrap';
import { AIServiceConfig, AIServiceConfigValidation, AIProvider } from './services';

interface QnAConfigBackendOverrideTabProps {
  config: AIServiceConfig;
  validation: AIServiceConfigValidation;
  onFieldChange: (field: keyof AIServiceConfig, value: unknown) => void;
  renderValidationFeedback: (field: keyof AIServiceConfigValidation) => React.ReactNode;
}

export function QnAConfigBackendOverrideTab({
  config,
  validation,
  onFieldChange,
  renderValidationFeedback,
}: QnAConfigBackendOverrideTabProps) {
  return (
    <div className="mt-3">
      <Form.Text className="text-muted d-block mb-3">
        <strong>Admin Only:</strong>
        {' '}
        Override backend AI parameters. When unchecked, the backend will use its default configuration.
      </Form.Text>

      {/* Provider Override */}
      <Form.Group className="mb-3">
        <Form.Check
          type="checkbox"
          label="Override Provider"
          checked={config.overrideProvider || false}
          onChange={(e) => onFieldChange('overrideProvider', e.target.checked)}
        />
        {config.overrideProvider ? (
          <>
            <Form.Control
              as="select"
              value={config.provider || ''}
              onChange={(e) => onFieldChange('provider', e.target.value as AIProvider)}
              isInvalid={validation.provider ? !validation.provider.isValid : false}
              className="mt-2"
            >
              <option value="">Select Provider</option>
              <option value={AIProvider.OpenAI}>OpenAI</option>
              <option value={AIProvider.Google}>Google</option>
            </Form.Control>
            {renderValidationFeedback('provider')}
            <Form.Text className="text-muted">
              Select AI provider for backend to use
            </Form.Text>
          </>
        ) : (
          <Form.Text className="text-muted d-block mt-1">
            Uses Backend Default
          </Form.Text>
        )}
      </Form.Group>

      {/* Model Override */}
      <Form.Group className="mb-3">
        <Form.Check
          type="checkbox"
          label="Override Model"
          checked={config.overrideModel || false}
          onChange={(e) => onFieldChange('overrideModel', e.target.checked)}
        />
        {config.overrideModel ? (
          <>
            <Form.Control
              type="text"
              value={config.model || ''}
              onChange={(e) => onFieldChange('model', e.target.value)}
              isInvalid={validation.model ? !validation.model.isValid : false}
              placeholder="e.g., gpt-4, gemini-2.0-flash"
              className="mt-2"
            />
            {renderValidationFeedback('model')}
            <Form.Text className="text-muted">
              Specify model name (supports OpenAI and Google models)
            </Form.Text>
          </>
        ) : (
          <Form.Text className="text-muted d-block mt-1">
            Uses Backend Default
          </Form.Text>
        )}
      </Form.Group>

      {/* Max Tokens Override */}
      <Form.Group className="mb-3">
        <Form.Check
          type="checkbox"
          label="Override Max Tokens"
          checked={config.overrideMaxTokens || false}
          onChange={(e) => onFieldChange('overrideMaxTokens', e.target.checked)}
        />
        {config.overrideMaxTokens ? (
          <>
            <Form.Control
              type="number"
              min={1}
              max={8192}
              value={config.maxTokens ?? ''}
              onChange={(e) => onFieldChange('maxTokens', parseInt(e.target.value, 10))}
              isInvalid={validation.maxTokens ? !validation.maxTokens.isValid : false}
              className="mt-2"
            />
            {renderValidationFeedback('maxTokens')}
            <Form.Text className="text-muted">
              Maximum tokens in response (1-8192)
            </Form.Text>
          </>
        ) : (
          <Form.Text className="text-muted d-block mt-1">
            Uses Backend Default
          </Form.Text>
        )}
      </Form.Group>

      {/* Temperature Override */}
      <Form.Group className="mb-3">
        <Form.Check
          type="checkbox"
          label="Override Temperature"
          checked={config.overrideTemperature || false}
          onChange={(e) => onFieldChange('overrideTemperature', e.target.checked)}
        />
        {config.overrideTemperature ? (
          <>
            <Form.Control
              type="number"
              min={0}
              max={2}
              step={0.1}
              value={config.temperature ?? ''}
              onChange={(e) => onFieldChange('temperature', parseFloat(e.target.value))}
              isInvalid={validation.temperature ? !validation.temperature.isValid : false}
              className="mt-2"
            />
            {renderValidationFeedback('temperature')}
            <Form.Text className="text-muted">
              Response creativity: 0.0 (precise) - 2.0 (creative)
            </Form.Text>
          </>
        ) : (
          <Form.Text className="text-muted d-block mt-1">
            Uses Backend Default
          </Form.Text>
        )}
      </Form.Group>

      {/* Document ID Override */}
      <Form.Group className="mb-3">
        <Form.Check
          type="checkbox"
          label="Override Document ID"
          checked={config.overrideDocumentId || false}
          onChange={(e) => onFieldChange('overrideDocumentId', e.target.checked)}
        />
        {config.overrideDocumentId ? (
          <>
            <Form.Control
              type="text"
              value={config.documentId || ''}
              onChange={(e) => onFieldChange('documentId', e.target.value)}
              isInvalid={validation.documentId ? !validation.documentId.isValid : false}
              placeholder="Google Docs document ID"
              className="mt-2"
            />
            {renderValidationFeedback('documentId')}
            <Form.Text className="text-muted">
              Google Docs document ID for FAQ context
            </Form.Text>
          </>
        ) : (
          <Form.Text className="text-muted d-block mt-1">
            Uses Backend Default
          </Form.Text>
        )}
      </Form.Group>
    </div>
  );
}
